import { computed, makeObservable, observable, runInAction } from 'mobx';
import { ApplicationSdk } from '../../../core/sdk';
import BaseEmployeeStore from '../../../shared/stores/baseEmployeeStore';
import { MatenRequestResponse } from '../../../shared';

interface DocumentButton {
    label: string;
    icon?: string;
    target_url: string;
    target_blank?: boolean;
}

class DocumentStore extends BaseEmployeeStore {
    public showSignDialog: boolean = false;
    public documentUuid: string | null = null;

    public pdfSignedUrl: string | null = null;
    public pdfFile: string | null = null;
    public pdfFileSigned: string | null = null;
    public documentHtml: string | null = null;
    public documentSignatureAccuracy: number | null = null;

    public documentSigned: boolean = false;
    public documentError: string | null = null;

    public documentButton: DocumentButton | null = null;

    constructor(sdk: ApplicationSdk) {
        super(sdk);

        makeObservable(this, {
            showSignDialog: observable,
            documentUuid: observable,
            pdfFile: observable,
            pdfFileSigned: observable,
            pdfSignedUrl: observable,
            documentHtml: observable,
            documentSigned: observable,
            documentError: observable,
            documentSignatureAccuracy: observable,
            outputDocumentError: computed,
        });
    }

    public async onInit(): Promise<void> {
        this.documentUuid =
            this.sdk.locationPublicUrl?.replace('/signabledocuments/', '').replace('/', '') || '';
        this.loadDocument();

        this.setMenuConfig({
            customHeader: {
                title: 'Unterzeichnen',
                icon: 'pi pi-pencil',
            },
        });

        this.updateFooterButton({
            label: 'Unterschreiben',
            icon: 'pi pi-pencil',
            onClick: () => this.handleSignDocument(),
        });
    }

    public async loadDocument(): Promise<void> {
        const response: MatenRequestResponse = await this.fetchDocumentInfo();
        if (!response.success) {
            runInAction(() => {
                this.documentError = response.error || 'ERR_UNKNOWN';
            });
        }
    }

    private handleSignDocument(): void {
        this.openSignDialog();
    }

    public openSignDialog(): void {
        this.showSignDialog = true;
    }

    public closeSignDialog(): void {
        this.showSignDialog = false;
    }

    public async signSignature(
        signatureDataUrl: string,
        accuracy: number | undefined = undefined,
        width: number,
        height: number
    ): Promise<void> {
        const result: MatenRequestResponse = await this.saveSignature(
            signatureDataUrl,
            accuracy,
            width,
            height
        );

        if (!result.success) {
            this.documentError = result.error || 'ERR_UNKNOWN';
        }

        this.closeSignDialog();
    }

    get isFooterButtonVisible(): boolean {
        if (this.documentError || this.documentSigned || this.isLoading) {
            return false;
        }
        return true;
    }

    get isFooterButtonEnabled(): boolean {
        return !!this.documentHtml && !this.pdfSignedUrl && !this.isLoading;
    }

    get isFooterButtonLoading(): boolean {
        return this.isLoading;
    }

    private async saveSignature(
        signatureDataUrl: string,
        accuracy: number | undefined = undefined,
        width: number,
        height: number
    ): Promise<MatenRequestResponse> {
        if (!this.documentUuid) {
            return { success: false, error: 'ERR_NO_DOCUMENT_UUID' };
        }

        this.setLoading(true);

        try {
            // store signature as base64 string
            const signResult = await this.sdk.fetchApi('signabledocuments', 'sign', {
                uuid: this.documentUuid,
                signature: signatureDataUrl,
                accuracy,
                width,
                height,
            });

            if (signResult?.status === 200) {
                const { url, redirect_url, button } = signResult.json;

                runInAction(() => {
                    this.setLoading(false);
                    this.pdfSignedUrl = url;
                    this.documentSigned = true;
                    this.documentButton = button || null;
                });

                if (redirect_url) {
                    window.location.href = redirect_url;
                }

                return { success: true, data: signResult.json };
            } else if (signResult?.status === 400) {
                return { success: false, error: signResult.json?.error };
            }
        } catch (error) {
            console.error('Fehler beim Speichern der Signatur:', error);
        }

        runInAction(() => {
            this.setLoading(false);
            this.documentSigned = false;
        });
        return { success: false, error: 'ERR_SIGNATURE_FAILED' };
    }

    public async fetchDocumentInfo(): Promise<MatenRequestResponse> {
        if (!this.documentUuid) {
            return { success: false, error: 'ERR_NO_DOCUMENT_UUID' };
        }
        this.setLoading(true);

        try {
            // diesen Endpunkt abfragen - jetzt mit Body-Parameter
            const documentInfoResult: any = await this.sdk.fetchApi('signabledocuments', 'get', {
                uuid: this.documentUuid,
            });

            this.setLoading(false);

            if (documentInfoResult?.status === 200) {
                const { html, required_accuracy, status, redirect_url, button } =
                    documentInfoResult.json;
                runInAction(() => {
                    this.documentButton = button || null;
                    if (status === 'signed') {
                        this.documentSigned = true;
                        if (redirect_url) {
                            window.location.href = redirect_url;
                        }
                    } else {
                        this.documentHtml = html;
                        this.documentSignatureAccuracy = required_accuracy;
                    }
                });
                return { success: true, data: documentInfoResult.json };
            } else if (documentInfoResult?.status === 400) {
                return { success: false, error: documentInfoResult.json?.error };
            }
        } catch (error) {
            console.error('Fehler beim Laden des Dokuments:', error);
        }

        runInAction(() => {
            this.setLoading(false);
        });

        return { success: false, error: 'ERR_DOCUMENT_NOT_FOUND' };
    }

    public async onDialogLeave(dialog: string, event: any = null): Promise<void> {}

    public onLeaveStore(): void {}

    get outputDocumentError(): string {
        if (this.documentError) {
            switch (this.documentError) {
                case 'ERR_NO_DOCUMENT_UUID':
                    return 'Link ungültig oder fehlerhaft.';
                case 'ERR_DOCUMENT_NOT_FOUND':
                case 'ERR_SIGNABLE_NOT_FOUND':
                    return 'Dokument nicht gefunden.';
                case 'ERR_DOCUMENT_EXPIRED':
                    return 'Dokument ist abgelaufen.';
                case 'ERR_NOT_SIGNABLE':
                    return 'Dokument kann nicht signiert werden.';
                case 'ERR_DOCUMENT_CANNOT_BE_SIGNED':
                    return 'Dokument kann nicht signiert werden.';
                case 'ERR_SIGNATURE_FAILED':
                case 'ERR_SIGNING_DOCUMENT':
                    return 'Signierung fehlgeschlagen.';
                case 'ERR_FETCHING_DOCUMENT':
                    return 'Laden fehlgeschlagen.';
                case 'ERR_INSERT_FAILED':
                    return 'Erstellung fehlgeschlagen.';
                default:
                    return 'Unbekannter Fehler aufgetreten.';
            }
        }

        return '';
    }
}

export default DocumentStore;
